import { useEffect, useCallback } from "react";
import packageJson from "../../package.json";

const CHECK_INTERVAL = 60 * 1000; // 1 minute
const RELOAD_KEY = "__reloaded_for_new_build__";

export default function VersionChecker() {
    const checkVersion = useCallback(async () => {
        try {
            const response = await fetch(`/version.json?t=${Date.now()}`);
            if (!response.ok) {
                console.warn(`[VersionChecker] Failed to fetch version.json: HTTP ${response.status}`);
                return;
            }

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                console.warn("[VersionChecker] Non-JSON response received (possibly index.html fallback)");
                return;
            }

            const data = await response.json();
            const localVersion = packageJson.version;
            const remoteVersion = data.version;

            console.log(`[VersionChecker] Check complete - Local: ${localVersion}, Remote: ${remoteVersion}`);

            // If versions match, clear the flag to allow future updates
            if (remoteVersion === localVersion) {
                sessionStorage.removeItem(RELOAD_KEY);
                return;
            }

            // If version mismatch is detected
            if (remoteVersion && remoteVersion !== localVersion) {
                // Prevent infinite loop: checks if we already tried reloading for this specific version
                const lastAttemptedVersion = sessionStorage.getItem(RELOAD_KEY);
                if (lastAttemptedVersion === remoteVersion) {
                    console.warn(
                        `[VersionChecker] Already auto-reloaded for version ${remoteVersion} but still on ${localVersion}. Stopping loop.`
                    );
                    return;
                }

                console.log(
                    `[VersionChecker] New version found: ${remoteVersion} (current: ${localVersion}). Auto-reloading...`
                );

                // Set flag to prevent loop
                sessionStorage.setItem(RELOAD_KEY, remoteVersion);

                // Clear Service Workers
                if ("serviceWorker" in navigator) {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    await Promise.all(regs.map((r) => r.unregister()));
                }

                // Clear Cache Storage
                if ("caches" in window) {
                    const names = await caches.keys();
                    await Promise.all(names.map((n) => caches.delete(n)));
                }

                // Force Reload
                window.location.reload();
            }
        } catch (error) {
            console.error("[VersionChecker] Error:", error.message);
        }
    }, []);

    useEffect(() => {
        // Initial check
        const initialTimeout = setTimeout(checkVersion, 5000);

        // Periodic check
        const interval = setInterval(checkVersion, CHECK_INTERVAL);

        // Visibility check
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                checkVersion();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            clearTimeout(initialTimeout);
            clearInterval(interval);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [checkVersion]);

    return null;
}
